import { useState, useEffect, useCallback, useRef } from 'react';

export const PAGE_SIZE = 50;

/**
 * Постраничный список с кнопкой «Загрузить ещё» — как в «Клиентах» (ClientsPage):
 * первая страница грузится при смене фильтров, следующая — с offset = уже загружено.
 *
 * Контракт API: `fetchPage({ limit, offset })` → `{ items, total, has_more }`
 * (списки склада: накладные, списания, пересорты, реестр инвентаризаций).
 *
 * `fetchPage` мемоизировать через useCallback по фильтрам — его смена и есть сигнал
 * перезагрузить список с начала. `reload()` после создания/приёмки/удаления грузит
 * заново столько строк, сколько уже показано (не меньше страницы), чтобы список
 * не схлопывался до 50 после каждого действия.
 *
 * `isLoading` — только до первого ответа: при смене фильтров таблица остаётся на
 * экране (шапка с фильтрами не «пропадает» под спиннером), строки подменяются.
 */
export function usePagedList(fetchPage, { pageSize = PAGE_SIZE, onError } = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const requestRef = useRef(0);   // номер последнего запроса — ответы устаревших игнорируем

  const reload = useCallback(async () => {
    const seq = ++requestRef.current;
    const limit = Math.min(500, Math.max(pageSize, itemsRef.current.length));
    try {
      const data = await fetchPage({ limit, offset: 0 });
      if (seq !== requestRef.current) return;
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      setHasMore(!!data?.has_more);
    } catch (e) {
      if (seq !== requestRef.current) return;
      onErrorRef.current?.(e);
    } finally {
      if (seq === requestRef.current) setIsLoading(false);
    }
  }, [fetchPage, pageSize]);

  const loadMore = useCallback(async () => {
    const seq = requestRef.current;
    setLoadingMore(true);
    try {
      const data = await fetchPage({ limit: pageSize, offset: itemsRef.current.length });
      if (seq !== requestRef.current) return;   // пока грузили, фильтры сменились
      setItems(prev => [...prev, ...(data?.items || [])]);
      setTotal(data?.total || 0);
      setHasMore(!!data?.has_more);
    } catch (e) {
      onErrorRef.current?.(e);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, pageSize]);

  // Смена fetchPage (= фильтров) — список с начала, одной страницей
  useEffect(() => {
    itemsRef.current = [];
    reload();
  }, [reload]);

  return { items, total, hasMore, isLoading, loadingMore, reload, loadMore };
}

/** Кнопка «Загрузить ещё» под таблицей — разметка как в «Клиентах». */
export function LoadMoreButton({ hasMore, loadingMore, onClick }) {
  if (!hasMore) return null;
  return (
    <div style={{ textAlign: 'center', marginTop: 20 }}>
      <button className="btn btn-secondary" onClick={onClick} disabled={loadingMore}>
        {loadingMore ? 'Загрузка...' : 'Загрузить еще'}
      </button>
    </div>
  );
}
